import React, { memo, useCallback, useMemo } from 'react';

import { Box } from '@mui/material';
import FormGeneratorSwitchCDS from '@sharedCR/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  FieldTypesEnum,
  FloodServiceKind,
  floodServiceDeterminationKinds,
  floodServiceKindOptions,
} from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION } from './LoanActionFloodServiceRow.constants';
import { getFloodProductOverrideOptions } from './LoanActionFloodServiceRow.utils';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import { getOptionCategoryName } from './LoanActionSelectOption.utils';

const emptyTaskTypesOptions = [];
const emptyFloodProductOptions = [];

const LoanActionFloodServiceRow = ({
  index,
  rootPath,
  taskTypesOptions = emptyTaskTypesOptions,
  floodProductOptions = emptyFloodProductOptions,
  isDisabled = false,
  isEditAllowed,
  isAutomatedActions = false,
  isSingleItem,
}) => {
  const { form, setInForm, setForm, setValidationErrors } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const kindFieldPath = `${fieldPath}.kind`;
  const rushFieldPath = `${fieldPath}.rush`;
  const pendingTaskTypeFieldPath = `${fieldPath}.pendingTaskType`;
  const productOverrideFieldPath = `${fieldPath}.productOverride`;
  const promptAtExecutionFieldPath = `${fieldPath}.promptAtExecution`;

  const forms = _.get(form, rootPath);
  const kind = _.get(form, kindFieldPath);
  const rush = _.get(form, rushFieldPath);
  const promptAtExecution = _.get(form, promptAtExecutionFieldPath);

  // Rush + the pending-task picker only apply to a full determination; FloodCheck is a fire-and-forget pre-screen.
  const isDetermination = floodServiceDeterminationKinds.includes(kind?.value);
  // Product override + the execution-time prompt apply only to a Determination order — Life-of-Loan Upgrade is a
  // derived product with no product choice.
  const isDeterminationKind = kind?.value === FloodServiceKind.DETERMINATION;

  const productOverrideOptions = useMemo(
    () => getFloodProductOverrideOptions(floodProductOptions),
    [floodProductOptions],
  );

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);

    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onChangeKind = useCallback(
    (__, value) => {
      setForm(prev => {
        const resetForm = _.omit(prev, [
          rushFieldPath,
          pendingTaskTypeFieldPath,
          productOverrideFieldPath,
          promptAtExecutionFieldPath,
        ]);

        // Product only ever appears once the user actively picks Determination, so it must resolve to
        // the same visible Company-default option the reverse transform uses for a loaded action —
        // never a blank Select.
        return value?.value === FloodServiceKind.DETERMINATION
          ? setIn(resetForm, productOverrideFieldPath, COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION)
          : resetForm;
      });
    },
    [
      setForm,
      rushFieldPath,
      pendingTaskTypeFieldPath,
      productOverrideFieldPath,
      promptAtExecutionFieldPath,
    ],
  );

  const onChangePromptAtExecution = useCallback(() => {
    setForm(prev =>
      setIn(
        _.omit(prev, [rushFieldPath]),
        productOverrideFieldPath,
        COMPANY_DEFAULT_FLOOD_PRODUCT_OPTION,
      ),
    );
  }, [setForm, productOverrideFieldPath, rushFieldPath]);

  const onTogglePromptAtExecution = useCallback(
    (__, checked) => {
      setInForm(promptAtExecutionFieldPath, checked);
      onChangePromptAtExecution();
    },
    [setInForm, promptAtExecutionFieldPath, onChangePromptAtExecution],
  );

  const onToggleRush = useCallback(
    (__, checked) => setInForm(rushFieldPath, checked),
    [setInForm, rushFieldPath],
  );

  const isRushHidden = isDeterminationKind && !!promptAtExecution;

  return (
    <Box className={wrapperClassname}>
      <Box className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          label="Kind"
          name={kindFieldPath}
          options={floodServiceKindOptions}
          type={FieldTypesEnum.SELECT}
          onChange={onChangeKind}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </Box>

      {isDetermination && (
        <DynamicValueInput
          disabled={isDisabled || !isEditAllowed}
          groupBy={getOptionCategoryName}
          label="Pending Task Type"
          name={pendingTaskTypeFieldPath}
          optionKey="taskTypeId"
          options={taskTypesOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isDeterminationKind && !isAutomatedActions && (
        <FormGeneratorSwitchCDS
          dataTestId={loanManagementTestIds.getFloodPromptAtExecutionSwitchId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          label="Allow order options at execution time"
          name={promptAtExecutionFieldPath}
          value={!!promptAtExecution}
          onChange={onTogglePromptAtExecution}
        />
      )}

      {isDetermination && !isRushHidden && (
        <FormGeneratorSwitchCDS
          dataTestId={loanManagementTestIds.getFloodRushSwitchId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          label="Rush (high-priority) order"
          name={rushFieldPath}
          value={!!rush}
          onChange={onToggleRush}
        />
      )}

      {isDeterminationKind && !promptAtExecution && (
        <DynamicValueInput
          disabled={isDisabled || !isEditAllowed}
          label="Product"
          name={productOverrideFieldPath}
          options={productOverrideOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}
    </Box>
  );
};

LoanActionFloodServiceRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  taskTypesOptions: PropTypes.array,
  floodProductOptions: PropTypes.array,
  isDisabled: PropTypes.bool,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionFloodServiceRow);
